/**
 * 🐑 **What a sheep wants** — T255, D117, D124.
 *
 * > ## **You do not write an animal. You write a list of rows.**
 *
 * 📖 **The standing spec is
 * [`Development/14_The_Animal_System.md`](../../../Development/14_The_Animal_System.md)**,
 * and this is its first land creature. **A sheep and a fish differ only in which
 * rows they have and what the numbers are** — which is the whole return on D117,
 * and it is collected here.
 *
 * ## 🌿 THE ONE LINE THAT TURNS WANDERING INTO GRAZING
 *
 * 🔴 **Minecraft's cow appears to GRAZE because its wander scorer gives GRASS
 * UNDERFOOT 10 AND EVERYTHING ELSE ZERO.** There is no grazing behaviour in that
 * game at all — `Minecraft/18` §2.2 calls that single line *"most of the
 * difference between an animal that random-walks and one that appears to be
 * grazing."*
 *
 * ✅ **AND THIS IS WHERE WE BEAT MINECRAFT WITHOUT TRYING.** The world is
 * generated from Iceland's own habitat raster, and **the block under a sheep's
 * feet IS that habitat, already resolved.** So ours does not score *"grass"* —
 * it scores the real habitat: **tún, gras, kjarrlendi, mýri, mói, lyng,
 * skógrækt, mosi** — and a flock drifts uphill because the ground there is
 * better grazing.
 *
 * ## 🔴 AND THE ONE RULE THIS TABLE MUST OBEY
 *
 * ⚠️ **EVERY BLOCK IN IT MUST BE SOLID**, because {@link grazingScore} reads
 * the block **under the feet** and a creature stands on top of a solid cell.
 *
 * 🔴 **It carried `kjarr` for weeks and `kjarr` IS NOT SOLID** — *you walk
 * through it, not on it* — **so that entry could never once have fired**,
 * while `birki`, the actual GROUND of birch country, was missing entirely.
 * 🎯 **The visible result was haugur asking whether sheep graze at all.**
 *
 * 👉 ***Somebody wrote the name of the PLANT where the name of the GROUND was
 * wanted*** — `06_Traps.md` rule 3, and **a test now refuses any entry that
 * cannot be underfoot**, so the class is closed rather than the instance.
 *
 * ## ⚠️ What is NOT here yet, said plainly
 *
 * ✅ **Panic when struck** — D125, `slaughter.ts`: a knife strike sends it
 * running. ⬜ **Tempt** — you
 * cannot hold food out. ⬜ **Follow-parent** — there are no lambs. ⬜ **Eating
 * the ground actually CHANGING it** — that is **T258**, and `Minecraft/18` §8.4
 * is clear it wants a rate haugur sets, not one a session picks.
 */

import { BLOCK } from './blocks.ts';
import { isBur, isBurPart } from './bur.ts';
import { isRum, isRumPart } from './rum.ts';
import { isPitBlock } from './pithouse.ts';
import type { Behaviour, Ground, Score, Sense, Species, Vec3 } from './creature.ts';
import { follow, placeOnGround, wander, withBody } from './creature.ts';
import { followMother, frolic, seekMate } from './breed.ts';
import { markGrazed } from './shear.ts';
import { baseBlockOf } from './states.ts';
import { led, tethered } from './lead.ts';
import type { SchoolRules } from './school.ts';
import { panic } from './slaughter.ts';

/**
 * 📏 **How tall a sheep stands, in metres, and it is a DIAL.**
 *
 * ⚠️ **An Icelandic sheep is short-legged and stocky**, and this is the whole
 * animal with its head up. 🔴 **NOT haugur's number yet** — exactly as the
 * þorskur's 80 cm was not until he was asked. **Size is judged against your own
 * body and no sheet can settle it**, so this wants his eye beside a 1.8 m
 * settler.
 */
export const SHEEP_HEIGHT = 0.85;

/** 📏 Nose to tail, measured off the model rather than chosen. */
export const SHEEP_LENGTH = 1.07;

/**
 * 📏 **How wide a sheep is for the purpose of BUMPING INTO THINGS — 0.7 m.**
 *
 * 🔴 **Not its 0.42 m across and not its 1.07 m long.** The footprint is a
 * SQUARE (see {@link withFootprint}), so one number has to answer both, and
 * **the honest choice is between them**: wide enough that a body no longer
 * sinks visibly into a wall, narrow enough that it is not refused gaps it
 * plainly walks through.
 *
 * 📖 **Mojang’s sheep is 0.9 × 1.3** and its footprint is likewise square and
 * likewise not its length.
 *
 * ⚠️ **A DIAL, and not haugur’s number.** 🔴 **Bigger is not safer**: every
 * increase refuses more ground, and a flock that cannot find anywhere legal
 * to stand is a worse fault than one standing slightly inside a bank.
 */
export const SHEEP_WIDTH = 0.7;

/**
 * 🚶 **Cruising speed, metres per second.**
 *
 * ⚠️ **A grazing sheep ambles.** Minecraft gives a cow `movement 0.25` and
 * strolls it at **×0.8**, and a real sheep crossing a field is doing well under
 * a metre a second. 🎯 **haugur asked for *"run a little"*, and that is the
 * PACE multiplier on a behaviour, not this number** — `Minecraft/18` §1.3:
 * *an animal that flees at 2× and browses at 0.8× has moods, with no mood
 * system.*
 */
export const SHEEP_SPEED = 0.9;

/**
 * 🌿 **What a sheep would rather be standing on.**
 *
 * 🔴 **THE ORDER IS FROM THE RESEARCH; THE NUMBERS ARE DIALS.**
 * [`Landnam/01_Resources.md`](../../../Development/Landnam/01_Resources.md):
 * **`tún` is the home field**, the best grass there is · **`lyng` is heather and
 * crowberry, fed to sheep in FODDER SHORTAGE** at Lónakot — so it is a poor
 * mouthful rather than a first choice · and 🔴 **`kjarr` is the one with
 * consequences**, because **D70's own note says Iceland lost its woods not
 * because trees were cut but because SHEEP ATE THE REGROWTH.**
 *
 * ⚠️ **Scoring it above bare ground is not the same as eating it**, and nothing
 * here changes a block — **T258**.
 *
 * 👉 **A block that is not in this list scores zero**, which is Minecraft's own
 * shape: one preference, everything else nothing.
 */
export const GRAZING: Readonly<Record<number, number>> = Object.freeze({
  [BLOCK.tun ?? -1]: 10,
  [BLOCK.gras ?? -2]: 8,
  /**
   * 🌳 **KJARRLENDI — the GROUND of birch country, and it was MISSING.**
   *
   * 🎯 **haugur, 2026-09-24:** *"sheep dont seem to graze (drop their head and
   * eat) should we do that?"* 🔴 **They already could. They just never stood
   * anywhere that scored.** 📏 **Measured on dev: every one of ten sheep was
   * standing on `birki`** — and `graze` refuses outright where the score is
   * zero, so **in birch country a sheep could never graze at all.**
   *
   * ⚠️ **6 — below mói and above mosi.** 📖 `Landnam/01_Resources.md` and the
   * habitat class itself: *birkiskógur OG kjarr* is **low, discontinuous scrub
   * with a sparse canopy**, so the floor of it is grass, low herbs and leaf
   * litter — **real grazing, and historically the best winter shelter there
   * was.** 🔴 It is not tún and it is not bare mói.
   */
  [BLOCK.birki ?? -3]: 6,
  [BLOCK.myri ?? -4]: 5,
  [BLOCK.moi ?? -5]: 4,
  [BLOCK.lyng ?? -6]: 3,
  /**
   * 🌲 **SKÓGRÆKT — planted forest. The same omission as `birki`, found with it.**
   *
   * ⚠️ **2, and low on purpose**: a closed plantation floor is poorer grazing
   * than open scrub. 🔴 **It is here because it is SOLID and a sheep can stand
   * on it** — and *a ground block a sheep can stand on and cannot eat from is a
   * place the flock will walk through and never settle*, which is a design
   * decision and must not be an accident.
   */
  [BLOCK.skograekt ?? -7]: 2,
  [BLOCK.mosi ?? -8]: 1,
  /**
   * 🌾 **SLÆGJA — a field just cut for hay (D131).** 2: the grass is in
   * somebody's barn, and what is left is a stubble. ⚠️ **Keyed by the BASE id;
   * {@link grazingScore} reads the base**, because cut mýri and cut gras are
   * two other numbers in the chunk and all three are the same short stalks.
   */
  [BLOCK.slaegja ?? -9]: 2,
});

/**
 * 🌿 **How much a sheep would like to stand here** — the scorer, and it is one
 * lookup.
 *
 * ⚠️ **It reads the block UNDER the feet, not the one it would stand IN.** A
 * creature stands on top of a solid cell, so the vegetation is half a metre
 * below the position being scored.
 */
export const grazingScore: Score = (ground: Ground, at: Vec3): number => {
  const under = ground.block(at.x, at.y - 0.5, at.z);
  if (under === null) return 0;
  return GRAZING[baseBlockOf(under)] ?? 0;
};

/**
 * 🌿 **Head down, eating** — the behaviour Minecraft calls `eat_block`.
 *
 * 🎯 **haugur, 2026-09-23:** *"raise, lower and turn it s hea"* and *"Yes"* to
 * eating the ground. 🔴 **This is what a lowered head is FOR**, and the question
 * that should have been asked before the first body was cut: *how can a sheep
 * eat grass if it cannot lower its head?*
 *
 * 📏 **Minecraft's numbers** (`Minecraft/18` §8.4): a **1-in-1000 chance per
 * evaluation**, about **once every 100 seconds**, and it takes **2 seconds**
 * during which the sheep is frozen — it holds `move` AND `look`.
 *
 * ⚠️ **Ours is far more likely, and that is deliberate.** Minecraft's rate is
 * tuned for decoration; 🎯 **haugur wants to WATCH these and judge the feel**,
 * and a sheep that grazes once every hundred seconds reads as a sheep that never
 * grazes. **This is a dial and the first one to reach for.**
 */
export function graze(priority: number, everySeconds: number): Behaviour {
  return {
    name: 'Graze',
    priority,
    /* 🔴 BOTH FLAGS. A grazing animal is FROZEN — it cannot walk off while its
       head is in the grass, and it is not watching you either. */
    flags: ['move', 'look'],
    wants(sense: Sense): boolean {
      const until = sense.self.memory.grazeUntil ?? 0;
      if (sense.now < until) return true;
      const next = sense.self.memory.grazeNext ?? 0;
      if (sense.now < next) return false;
      /* ⚠️ Only bother where there is something worth eating. */
      if (grazingScore(sense.ground, sense.self.at) <= 0) {
        sense.self.memory.grazeNext = sense.now + everySeconds * 0.5;
        return false;
      }
      sense.self.memory.grazeUntil = sense.now + GRAZE_SECONDS;
      sense.self.memory.grazeNext = sense.now + everySeconds;
      /* ✂️ D126 — a shorn sheep grows its wool back only once it has EATEN. */
      markGrazed(sense.self);
      return true;
    },
    act(): { goal: null; lookAt: null; hold: true } {
      /* 🧍 `hold` — STAND STILL. `goal: null` alone means *pick somewhere new*,
         and until 2026-10-01 a grazing sheep walked off at full pace. */
      return { goal: null, lookAt: null, hold: true };
    },
  };
}

/**
 * ⏱️ **8 seconds — haugur's number, 2026-10-01**, and four times Minecraft's.
 *
 * 🎯 *"they drop their head for just a second and then keep walking, in nature
 * sheep stand still longer while thei graze"* → *"head stays down 8 sec starts
 * every 26 secs"*. 📖 Minecraft's 2.0 s (both editions, `Minecraft/18` §8.4) is
 * tuned for decoration; a real sheep grazes most of its day.
 */
export const GRAZE_SECONDS = 8;

/**
 * 👀 **Watching somebody go past — the cheapest charm in the game.**
 *
 * ✅ **Confirmed in BOTH editions**, which is rare enough to say
 * (`Minecraft/18` §1.5): `DEFAULT_PROBABILITY` **0.02**, `lookDistance`
 * **6.0**, `lookTime` **40–80 ticks = 2 to 4 seconds**.
 *
 * 🔴 **IT HOLDS `look` ONLY, AND THAT IS THE WHOLE POINT.** The animal keeps
 * walking while it stares. ⚠️ `Minecraft/18` §1.3: *a cow that walks somewhere
 * while turning its head to watch you is two goals that have never heard of each
 * other* — **the difference between an animal and a vehicle.**
 *
 * 👉 **And the unpredictability is the charm.** A 2% roll means an animal in
 * range looks at you **about every five seconds, for two to four** — so it is
 * not *"sheep look at players"*, it is ***"a sheep will probably look at you
 * soon."***
 */
/**
 * 👀 **Looking about, at nothing in particular — and WITHOUT IT A SHEEP IS A
 * STATUE FROM THE NECK UP.**
 *
 * 🎯 **haugur, 2026-09-23, watching them:** *"head does not move uo or down or
 * sideways, neck looks locked."*
 *
 * 🔴 **HE WAS RIGHT AND THE CAUSE WAS AN ABSENCE.** The head was cut, pivoted
 * and animated, and **{@link lookAtPlayer} was the only thing that could ever
 * drive it** — which needs a player within a few metres. ⚠️ **Our sheep are
 * spawned 8 to 22 m away and wander within 10**, so in practice **nobody was
 * ever close enough and the neck never moved at all.**
 *
 * ✅ **Minecraft has exactly this and calls it `random_look_around`**
 * (`Minecraft/18` §1.5): the same **0.02** chance, a direction held for about
 * **one to two seconds**.
 *
 * ⚠️ **AND ONE THING FROM THE RESEARCH IS DELIBERATELY NOT COPIED.** Java's
 * `RandomLookAroundGoal` holds **`MOVE` as well as `LOOK`**, so idle
 * head-turning blocks walking — 18 §1.5 calls it out as *"one genuine
 * surprise"* and says it works only because it sits at the very bottom of the
 * list. 🔴 **Ours holds `look` alone**, which is what the flag is for.
 */
/**
 * 👀 **How far to either side an idle sheep glances, radians — about 55°.**
 *
 * 🔴 **It MUST stay under the head clamp**, or an idle glance is just the head
 * held at its limit. 📏 The clamp is 75°; this is comfortably inside it, so the
 * animal looks about rather than straining.
 */
export const IDLE_GLANCE = 0.96;

export function lookAround(priority: number, reach: number): Behaviour {
  return {
    name: 'LookAround',
    priority,
    flags: ['look'],
    wants(sense: Sense): boolean {
      const until = sense.self.memory.idleUntil ?? 0;
      if (sense.now < until) return true;
      if (sense.self.rng() > 0.02) return false;
      /* 🔴 A direction, remembered — not a fresh one every tick, which would be
         a head having a fit rather than an animal looking about. */
      /**
       * 👀 **AN IDLE GLANCE IS RELATIVE TO THE BODY, NOT TO THE WORLD.**
       *
       * 🎯 **haugur, 2026-09-24, watching the first rigged flock:** *"they move
       * their head from left to rights, they seldom look straight forward and if
       * the do the stop their only a very short tim, so they are olways kinda
       * looking to the side, looks a little weird."*
       *
       * 🔴 **THE CAUSE: this picked a uniformly random WORLD angle.** A head may
       * only lead its body by 75°, so **most of a full circle is outside the
       * clamp** — and a target behind the animal does not read as *looking
       * behind*, it reads as **the head pinned at its limit**. 📏 **Over half of
       * a uniform circle lands outside ±75°**, which is exactly *"olways kinda
       * looking to the side."*
       *
       * ✅ **So the glance is chosen inside a cone about the heading**, a little
       * narrower than the clamp, and it passes through straight-ahead on the way
       * rather than slamming into a limit.
       *
       * ⚠️ **A DIAL and not haugur’s number.** 🔴 **It must stay UNDER the head
       * clamp** — at or past it the animal is back at the limit and this whole
       * paragraph is undone.
       */
      const cone = IDLE_GLANCE;
      sense.self.memory.idleAngle = sense.self.heading + (sense.self.rng() * 2 - 1) * cone;
      sense.self.memory.idleUntil = sense.now + 1 + sense.self.rng();
      return true;
    },
    act(sense: Sense): { lookAt: Vec3 } {
      const angle = sense.self.memory.idleAngle ?? 0;
      return {
        lookAt: {
          x: sense.self.at.x + Math.cos(angle) * reach,
          /* ⚠️ Roughly its own eye height, so it looks ACROSS the hillside
             rather than at the sky. */
          y: sense.self.at.y + SHEEP_HEIGHT * 0.7,
          z: sense.self.at.z + Math.sin(angle) * reach,
        },
      };
    },
  };
}

export function lookAtPlayer(priority: number, within: number): Behaviour {
  return {
    name: 'Watch',
    priority,
    flags: ['look'],
    wants(sense: Sense): boolean {
      const until = sense.self.memory.watchUntil ?? 0;
      if (sense.now < until) return sense.nearestPlayer !== null;
      if (sense.nearestPlayer === null) return false;
      if (sense.nearestPlayer.distance > within) return false;
      /* ⚠️ A chance, not a timer. A fixed interval is visible within a minute. */
      if (sense.self.rng() > 0.02) return false;
      sense.self.memory.watchUntil = sense.now + 2 + sense.self.rng() * 2;
      return true;
    },
    act(sense: Sense): { lookAt: Vec3 | null } {
      return { lookAt: sense.nearestPlayer?.at ?? null };
    },
  };
}

/**
 * 🐑 **A flock**, and it is the fish's own machinery unchanged.
 *
 * 📖 `14_The_Animal_System.md` §④ settled this as the standing architecture for
 * **every group this game will ever have**: 🔴 **one leader that thinks,
 * followers that do not** — *"which is also how thousands of animals become
 * affordable."* ✅ **It generalises straight from a shoal to a flock**, and
 * nothing here had to be written to make that true.
 */
export const SHEEP_FLOCK: SchoolRules = {
  /* 📏 Java scans a ±8 block box for a shoal; a flock gathers at about the same
     distance. */
  joinRadius: 9,
  /* ⚠️ Comfortably past the join radius, or an animal joins and breaks in the
     same breath and the flock flickers. */
  breakRadius: 13,
  /* 📏 Java re-decides a school every 200–219 ticks. 🔴 That timer is why a
     leaderless group takes about ten seconds to re-form — a visible scatter and
     gather, which is what it should look like. */
  everySeconds: 10.5,
  maxFollowers: 7,
  /**
   * 📏 **Spacing — and sheep graze FAR looser than fish shoal.**
   *
   * ⚠️ **A fish keeps station at about 1.9 body lengths and reads as a shoal.**
   * 🔴 **A flock packed that tight reads as one object with legs** — which is
   * the fault haugur named on the fish *before* it was built: *"all the same
   * size, all move the same."* **Three body lengths is a field of sheep.**
   */
  spacing: SHEEP_LENGTH * 3,
};

/**
 * 🐑 **The sheep.**
 *
 * 🔴 **THE ORDER OF THIS LIST IS THE DESIGN**, and every gap does work:
 *
 * | | |
 * |---|---|
 * | 🌿 **`Graze` above everything** | a sheep with its head down **stays put** — it is the one thing that stops a flock milling for ever |
 * | 🐑 **`Follow` above `Wander`** | Java's `canRandomSwim()` rule for free: **a follower never picks a destination at all** |
 * | 👀 **`Watch` holds only `look`** | so it runs **at the same time** as walking, and costs nothing |
 *
 * ⚠️ **A SHEEP IS PASSIVE — D117.** 🎯 **haugur:** *"a sheep does not run away
 * from you until you hit it, i like that."* **So there is no `Wary` row here,
 * and its absence is a decision rather than an omission.**
 */
export function sheep(): Species {
  return {
    kind: 'kind',
    behaviours: [
      /* 🏃 ABOVE GRAZING — D125. *"A sheep does not run away from you until you
         hit it"*: nothing a sheep wants outranks having been struck. ⚠️ −1, so
         the existing numbers below did not have to move. */
      panic(-1),
      /* 🪢 D128 — back inside its circle before anything calmer. ⚠️ Only when
         OUTSIDE it: inside, it grazes and wanders like any sheep — the wander
         spot is chosen inside the circle (`chooseDestination`'s `home`). */
      led(-0.6),
      tethered(-0.5),
      /* 💞🐣 D130 — above grazing and below everything that holds a sheep in
         place. A ready sheep walks to its mate; a lamb left behind goes back to
         its mother; a lamb near her sometimes bursts into play. ⚠️ Each wants
         only what the SERVER hands it (`mate`, `mother`), so a sheep with
         neither never runs them. */
      seekMate(-0.4),
      followMother(-0.3),
      frolic(-0.2),
      /* 🌿 Grazing wins, because a head in the grass cannot also be walking. */
      /* 🎯 haugur, 2026-10-01: starts every 26 s, head down for 8. */
      graze(0, 26),
      follow(5, SHEEP_FLOCK),
      /* 🚶 The floor of the list. ⚠️ Re-rolls about every six seconds, which is
         Minecraft's own `interval: 120` at 20 ticks a second. */
      wander(9, 6),
      /* 👀 Bottom of the list and a different flag, so it never blocks a step. */
      /**
       * 👀 **12 m, not Mojang's 6 — and the reason is our own spawner.**
       *
       * ⚠️ **Minecraft uses 6 because you are constantly bumping into animals.**
       * 🔴 **Ours are dropped 8 to 22 m away and wander within 10**, so at 6 m a
       * player was almost never inside the range and **the head never turned.**
       * 👉 ***A borrowed number needs the world it was measured in.***
       */
      lookAtPlayer(10, 12),
      /* 👀 The floor of the look list, so a sheep with nobody near still has a
         head. ⚠️ BELOW `Watch`: somebody arriving wins. */
      lookAround(11, 6),
    ],
    physique: {
      speed: SHEEP_SPEED,
      /* ⚠️ A sheep turns its whole body and it takes a moment. A creature that
         snaps round reads as a machine — most of what *"like robots"* was. */
      turn: 2.2,
      /**
       * 🪜 **1 m UP — a hop — and 3 m DOWN.** 🔴 **It was 0.6, Minecraft's
       * `step_height`, and in a world of whole-metre blocks that climbed
       * nothing**: measured 2026-09-29, a sheep walked into every 1 m step and
       * stopped, up or down. Minecraft's mobs JUMP a block; this is the jump.
       *
       * 🚧 **And it is why a pen wall is 1.5 m** (`grjotgardur`, haugur's
       * choice): higher than the hop, as Minecraft's fence is — its 1.5 m
       * collision on a 1 m look is exactly this sum.
       */
      step: 1,
      drop: 3,
    },
    /* 📏 Minecraft's `random_stroll` is `xz_dist: 10`, and both editions agree.
       ⚠️ A dial, and the first to reach for if a flock reads as static. */
    wander: { radius: 10, candidates: 10 },
    school: SHEEP_FLOCK,
    /**
     * 🚶 **A walker stands on whatever the ground turns out to be.**
     *
     * 🔴 **MEASURE THE ROOM FIRST AND CHOOSE INSIDE IT.** The fish's worst fault
     * was choosing a height and then clamping it, which stood a whole shoal on
     * one plane. ⚠️ **A walker that picks its own height is wrong nineteen times
     * out of twenty** — its candidates land in mid-air or inside the hill.
     *
     * 📏 **`rise` 4 m**, so it can want somewhere up a bank — which is
     * `Minecraft/18` §2.3's point about `y_dist`: *the parameter that makes
     * wandering follow the landscape instead of ignoring it.*
     */
    place: placeOnGround(4, Math.ceil(SHEEP_HEIGHT)),
    score: grazingScore,
    /**
     * 🧱 **The whole body has to fit, not just the middle of it.**
     *
     * 🎯 **haugur:** *"look at the sheep half way in the wall."* 📏 **A sheep
     * is 1.07 m long, and until now only its CENTRE was ever tested** — so
     * half of one could be inside a terrace and nothing objected.
     *
     * 🪜 **Clear at every corner, but standing on something at only ONE** —
     * 2026-09-29. Ground under all four corners made every 1 m step
     * impassable both ways; see `withBody`.
     */
    fits: withBody(sheepClear, sheepSupported, SHEEP_WIDTH),
    /**
     * 🧍 **And it is solid to you and to other sheep** — T274, T262.
     *
     * 🔴 **The SAME width as the footprint above**, on purpose: *how big is a
     * sheep for bumping into things* has one answer, and a second number for
     * bumping into bodies would drift from the one for walls.
     */
    body: { width: SHEEP_WIDTH, height: SHEEP_HEIGHT },
  };
}

/**
 * 🚶 **Could a sheep be here?** Solid under the feet, clear where its body is,
 * and **not in the sea.**
 *
 * ⚠️ **Water is a refusal rather than a cost**, and that is honest rather than
 * finished: 🔴 **water does nothing to any creature or player in this game
 * (T229)**, so a sheep that could enter it would wade about at full speed and
 * read as a swimming sheep. **A flat no is the smaller lie until T229 lands.**
 */
export function standsOn(ground: Ground, at: Vec3): boolean {
  return sheepClear(ground, at) && sheepSupported(ground, at);
}

/**
 * 🧱 **Is this point of a sheep in clear air** — not in a wall, not in the sea,
 * head room above. The half of {@link standsOn} asked at EVERY point of the
 * body by `withBody`.
 */
export function sheepClear(ground: Ground, at: Vec3): boolean {
  if (ground.water(at.x, at.y, at.z)) return false;
  if (ground.solid(at.x, at.y, at.z)) return false;
  return !ground.solid(at.x, at.y + SHEEP_HEIGHT * 0.8, at.z);
}

/**
 * 🦶 **Would a sheep stand on what is under this point?** The half of
 * {@link standsOn} that only ONE point of the body needs — see `withBody`.
 */
export function sheepSupported(ground: Ground, at: Vec3): boolean {
  if (!ground.solid(at.x, at.y - 0.5, at.z)) return false;
  /**
   * 🌳 **A SHEEP DOES NOT STAND IN A TREE.**
   *
   * 🎯 **haugur, 2026-09-24:** *"they walk on the ground but on top of kjarr,
   * when the walk on top of kjarr the kina look like they are floating a
   * little, they shoould walk through kjarr."*
   *
   * 🔴 **It was not kjarr — kjarr is already `solid: false` and nothing can
   * stand on it. It was `birkilauf`, the birch CANOPY, which is solid** and
   * which a sheep could therefore legitimately climb onto. 📏 **Measured on
   * dev: 7 of 10 sheep stood 0.4–2.8 m above the terrain under them, and 8 of
   * 10 had not moved a centimetre in forty seconds** — stranded up a tree.
   *
   * ⚠️ **Leaves stay solid for the PLAYER.** Minecraft climbs its trees too,
   * and you must be able to cut a canopy block. 🔴 **This is a rule about what
   * an ANIMAL will walk on**, which is a different question — the same shape
   * as `isSolid` against `canBeHit`, recorded in `blocks.ts`.
   */
  return isFooting(ground.block(at.x, at.y - 0.5, at.z));
}

/**
 * 🦶 **WILL AN ANIMAL PUT ITS FEET ON THIS?** — and it is NOT the same question
 * as *is it solid*.
 *
 * 🎯 **haugur, 2026-09-24, after the first fix:** *"much better, there is still
 * one standing on top of my skemma and búr."* 📷 **His picture shows a sheep
 * hanging in the air over a gable.**
 *
 * 🔴 **IT IS NOT HOVERING — IT IS STANDING ON THE BLOCK BOX.** A turf house is
 * ⚠️ **a MODEL drawn over a solid block footprint**, and the box is taller than
 * the roof it draws. 📖 `06_Traps.md`: ***a block’s face and a model’s wall are
 * two different surfaces*** — the same rule that hung a carved board 39 cm off
 * a wall. **So a creature standing legally on the block stands visibly above
 * the roof.**
 *
 * 🔴 **AND A SHEEP ON A ROOF IS WRONG EVEN WHERE THE GEOMETRY AGREES.** A house
 * is not pasture. ⚠️ **It reached the same list as the canopy for the same
 * reason** — the two faults are one fault, and the first fix was too narrow:
 * it named the ONE block that had bitten rather than the QUESTION being asked.
 * 👉 ***That is `06_Traps.md` rule 3 arriving from the other side: a rule
 * derived from one example is wrong the day a second one shares it.***
 *
 * ⚠️ **All of these stay solid for the PLAYER.** You climb your own roof, you
 * cut a canopy block. **Whether an ANIMAL will walk on it is a different
 * question** — the same shape as `isSolid` against `canBeHit`.
 */
export function isFooting(id: number | null): boolean {
  if (id === null) return false;
  /* 🌳 A tree. */
  if (id === BLOCK.birkilauf) return false;
  /* 🏠 A building — the door block of any of the three houses, and every
     other cell of its footprint. ⚠️ Both, or a sheep stands on the half of
     the roof that is not the door. */
  if (isBur(id) || isBurPart(id)) return false;
  /* 🥣 The jata — T286. A sheep eats FROM a manger and never stands in it;
     the garðaband existed because real ones tried. Every fill state, so the
     base id is what is asked. */
  if (baseBlockOf(id) === BLOCK.jata) return false;
  /* 🛏️ The rúm — D133. A bed is for a person; both cells, head and foot. */
  if (isRum(id) || isRumPart(id)) return false;
  /* 🏠 D135 — the pit house: walls, ceiling, step, oven, bench and door. A
     sheep may wander IN through an open door; it never stands ON the house. */
  if (isPitBlock(id)) return false;
  return true;
}

/**
 * 📐 **75° — how far a head may LEAD its body.** Mojang’s number.
 *
 * 🔴 **IT LIVES IN `shared` BECAUSE BOTH HALVES NEED IT AND THEY DISAGREED.**
 * The client clamps the drawn head to it; **the brain has to choose a glance
 * INSIDE it**, or every idle look lands on the limit. 🎯 **That is exactly what
 * haugur saw:** *"they are olways kinda looking to the side, looks a little
 * weird."*
 *
 * ⚠️ **It used to live only in the client**, so nothing on the server could
 * know what it was — *a second copy is how `fishshot.mjs` invented a thin
 * þorskur that was never thin.*
 */
export const HEAD_LEAD = (75 * Math.PI) / 180;
